import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { sameItems, sameRows } from '@/hooks/selectEqual';
import { useAutosaveField } from '@/hooks/useAutosave';
import { usePhotoCapture } from '@/hooks/usePhotoCapture';
import { ANON_IDENTITY, usePort, useRowboat, useSelect } from '@/rowboat';
import type { ItemRow } from '@/schema/estate';
import { resolveCategory } from '@/services/categoryService';
import { FLAG_NAMES, hasFlag, toggleFlag } from '@/services/flags';
import { listItemGroups } from '@/services/itemGroupService';
import { assignNumber } from '@/services/itemNumbers';
import {
  aliasesOf,
  archiveItem,
  getItem,
  type ItemBinding,
  isBlankItem,
  itemsByEstate,
  setCategory,
  setItemGroup,
  updateName,
} from '@/services/itemService';
import { IDENTIFY_CATEGORIES } from '../../shared/identifyCategories';
import { ItemEnrichment } from './ItemFields';
import { PhotoGrid } from './PhotoGrid';
import { PhotoCaptureButton } from './PhotoInput';
import { FieldSuggestions, IdentifyStatus } from './Suggestions';
import { Field, Page, TextInput, TrashIcon, useEscape, ViewOnlyNote } from './ui';

const sameItem = (a: ItemRow | undefined, b: ItemRow | undefined) =>
  a === b ||
  (!!a &&
    !!b &&
    a.id === b.id &&
    a.owner_group_id === b.owner_group_id &&
    a.estate_id === b.estate_id &&
    a.item_group_id === b.item_group_id &&
    a.category_id === b.category_id &&
    a.inventory_number === b.inventory_number &&
    a.name === b.name &&
    a.description === b.description &&
    a.condition_text === b.condition_text &&
    a.provenance === b.provenance &&
    a.dimensions === b.dimensions &&
    a.materials === b.materials &&
    a.status === b.status &&
    aliasesOf(a).join('|') === aliasesOf(b).join('|'));

// Flag state must be a selected snapshot: a toggle changes the item_tag/tag tables, which
// none of the other selectors watch, so without this ItemDetail would never re-render and
// `aria-pressed` would go stale.
const sameFlags = (a: Array<readonly [string, boolean]>, b: Array<readonly [string, boolean]>) =>
  a.length === b.length && a.every((f, i) => f[0] === b[i][0] && f[1] === b[i][1]);

const UNBOUND = 'Pick a group first';

type Nav = {
  onBack: () => void;
  onAdd: (from: ItemBinding) => Promise<void>;
  onMenu: () => void;
};

export function ItemDetail({
  itemId,
  readOnly = false,
  adding = false,
  ...nav
}: Nav & { itemId: string; readOnly?: boolean; adding?: boolean }) {
  const graph = useRowboat();
  const item = useSelect(() => getItem(graph, itemId), sameItem);
  useEscape(nav.onBack);
  if (!item) {
    return (
      <section aria-label="Item detail">
        <p>Item not found</p>
        <button type="button" onClick={nav.onBack}>
          Back
        </button>
      </section>
    );
  }
  return <ItemDetailBody item={item} readOnly={readOnly} adding={adding} {...nav} />;
}

function ItemDetailBody({
  item,
  readOnly,
  adding,
  onBack,
  onAdd,
  onMenu,
}: Nav & { item: ItemRow; readOnly: boolean; adding: boolean }) {
  const graph = useRowboat();
  const { author } = usePort();
  const actor = author ?? ANON_IDENTITY;
  // Parent FKs are nullable in the row type; rows from getItem always carry them, so the
  // app's "" sentinel is the type-level fallback.
  const estateId = item.estate_id ?? '';
  const itemGroupId = item.item_group_id ?? '';
  const bound = itemGroupId !== '';
  const locked = !bound || readOnly;
  const itemGroups = useSelect(() => listItemGroups(graph, estateId), sameRows);
  const categoryName = useSelect(() =>
    item.category_id ? (graph.category(item.category_id)?.$data.name ?? '') : '',
  );
  // A category made by hand before the list was fixed stays pickable on its own items.
  const categoryNames: string[] = IDENTIFY_CATEGORIES.map((c) => c.name);
  if (categoryName && !categoryNames.includes(categoryName)) categoryNames.unshift(categoryName);
  const items = useSelect(() => itemsByEstate(graph, estateId), sameItems);
  const flags = useSelect(
    () => FLAG_NAMES.map((flag) => [flag, hasFlag(graph, item.id, flag)] as const),
    sameFlags,
  );

  // draftKey per field: text typed before a signup identity flip (which remounts the
  // whole tree with a fresh graph) survives in the hook's draft map and rehydrates.
  const name = useAutosaveField(
    item.name,
    (v) => updateName(graph, item.id, v),
    `item:${item.id}:name`,
  );
  const capture = usePhotoCapture({ itemId: item.id, groupId: item.owner_group_id, author: actor });
  const [error, setError] = useState('');
  const run = (label: string, commit: () => Promise<unknown>) =>
    void commit().catch((err) => setError(err instanceof Error ? err.message : `${label} failed`));

  // What the user entered here, saved or not. Leaving checks this before the store, which
  // may not yet show text whose save the unmount flush has only just started.
  const texts = useRef<Record<string, string>>({});
  texts.current.name = name.value;
  const onText = useCallback((field: string, text: string) => {
    texts.current[field] = text;
  }, []);
  const photoTaken = useRef(false);
  const flagsSet = useRef<Record<string, boolean>>({});
  const flagsShown = useRef(flags);
  flagsShown.current = flags;
  const readOnlyNow = useRef(readOnly);
  readOnlyNow.current = readOnly;

  // Leaving (Back, save-and-next, any unmount) archives an item the user entered nothing
  // into, unless they are a Viewer, who may be looking at someone's new item. The timeout
  // skips React StrictMode's simulated unmount, which re-runs setup (re-arming `alive`)
  // before the timeout fires.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    const id = item.id;
    return () => {
      alive.current = false;
      setTimeout(() => {
        if (alive.current || readOnlyNow.current) return;
        const entered =
          Object.values(texts.current).some((t) => t.trim() !== '') ||
          photoTaken.current ||
          flagsShown.current.some(([flag, on]) => flagsSet.current[flag] ?? on);
        if (!entered && isBlankItem(graph, id)) void archiveItem(graph, id).catch(() => undefined);
      }, 0);
    };
  }, [graph, item.id]);

  // Distinct recent names, newest first.
  const nameSuggestions: string[] = [];
  {
    const seen = new Set<string>();
    for (const row of [...items].reverse()) {
      if (nameSuggestions.length >= 30) break;
      const n = row.name.trim();
      if (n && !seen.has(n.toLowerCase())) {
        seen.add(n.toLowerCase());
        nameSuggestions.push(n);
      }
    }
  }

  const pickItemGroup = (id: string) =>
    run('Group', async () => {
      const itemGroup = itemGroups.find((r) => r.id === id);
      if (!itemGroup) return;
      await setItemGroup(graph, item.id, id);
      await assignNumber(graph, item.id, itemGroup.prefix);
    });
  const pickCategory = (name: string) =>
    run('Category', async () => {
      const id = await resolveCategory(graph, {
        estateId,
        name,
        groupId: item.owner_group_id,
        author: actor,
      });
      await setCategory(graph, item.id, id);
    });

  const toggle = (flag: string, pressed: boolean) => {
    flagsSet.current[flag] = !pressed;
    // Flags are local-first like everything else: anonymous toggles land in the local
    // store under the anon identity and sync on signup. The author field is unused by
    // flag writes; it only satisfies the service signature.
    run('Flag', () =>
      toggleFlag(graph, {
        itemId: item.id,
        estateId,
        groupId: item.owner_group_id,
        author: actor,
        flag,
      }),
    );
  };

  const remove = () => {
    if (!window.confirm(`Delete ${item.inventory_number || 'this item'}?`)) return;
    setError('');
    archiveItem(graph, item.id).then(onBack, () => setError('Could not delete the item'));
  };

  const saveAndNext = () => {
    name.commit();
    setError('');
    onAdd({ itemGroupId }).catch(() => setError('Could not add the item'));
  };

  const aliases = aliasesOf(item);

  return (
    <Page label="Item detail">
      <header className="sticky top-0 z-20 -mx-4 flex items-center gap-1 bg-surface-secondary px-2 py-1">
        <HeaderButton label="Back" onClick={onBack}>
          ←
        </HeaderButton>
        <div className="min-w-0 flex-1 px-1">
          <h2 className="truncate text-xl font-bold text-content-primary">
            {item.inventory_number || 'New item'}
          </h2>
          {aliases.length > 0 && (
            <p className="truncate text-xs text-content-tertiary">{aliases.join(', ')}</p>
          )}
        </div>
        {!readOnly && (
          <>
            <HeaderButton label="Delete item" onClick={remove}>
              <TrashIcon />
            </HeaderButton>
            <PhotoCaptureButton
              itemId={item.id}
              previews={capture.previews}
              saved={capture.saved}
              error={capture.error}
              disabled={!bound}
              onFiles={(files) => {
                if (files.length > 0) photoTaken.current = true;
                void capture.takePhotos(files);
              }}
              onDone={() => void capture.identify()}
            />
            <HeaderButton
              label="Save and add next item"
              title={bound ? undefined : UNBOUND}
              disabled={!bound || adding}
              onClick={saveAndNext}
            >
              +
            </HeaderButton>
          </>
        )}
      </header>
      {readOnly && <ViewOnlyNote />}
      <PhotoGrid itemId={item.id} previews={capture.previews} label="Gallery" readOnly={readOnly} />
      {capture.error && <p role="alert">{capture.error}</p>}
      <IdentifyStatus itemId={item.id} readOnly={readOnly} />
      {itemGroups.length === 0 && !readOnly && (
        <button
          type="button"
          onClick={onMenu}
          className="text-left text-sm text-primary-700 underline"
        >
          No groups yet. Add them in the Project menu.
        </button>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Group">
          <Picker
            label="Group"
            value={itemGroupId}
            options={itemGroups}
            placeholder="Pick a group"
            disabled={readOnly}
            onPick={pickItemGroup}
          />
        </Field>
        <Field label="Category">
          <Picker
            label="Category"
            value={categoryName}
            options={categoryNames.map((name) => ({ id: name, name }))}
            placeholder="Pick a category"
            disabled={readOnly}
            onPick={pickCategory}
          />
        </Field>
      </div>
      <FieldSuggestions itemId={item.id} field="category" readOnly={readOnly} />
      <Field label="Item">
        <TextInput
          aria-label="Name"
          value={name.value}
          disabled={locked}
          onChange={(e) => name.onChange(e.target.value)}
          onBlur={() => name.commit()}
          placeholder={locked ? (readOnly ? '' : UNBOUND) : 'e.g. oak armchair'}
          list="inspector-names"
          autoComplete="off"
        />
        <datalist id="inspector-names">
          {nameSuggestions.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
      </Field>
      <FieldSuggestions itemId={item.id} field="name" readOnly={readOnly} />
      <ItemEnrichment item={item} disabled={locked} readOnly={readOnly} onText={onText} />
      <div className="flex flex-wrap gap-2">
        {flags.map(([flag, pressed]) => (
          <button
            key={flag}
            type="button"
            aria-pressed={pressed}
            disabled={locked}
            onClick={() => toggle(flag, pressed)}
            className={`min-h-9 rounded-full px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
              pressed
                ? 'bg-primary-600 text-white'
                : 'border border-divider bg-surface-primary text-content-secondary hover:bg-interactive-hover'
            }`}
          >
            {flag}
          </button>
        ))}
      </div>
      {error && <p role="alert">{error}</p>}
    </Page>
  );
}

function Picker({
  label,
  value,
  options,
  placeholder,
  disabled,
  onPick,
}: {
  label: string;
  value: string;
  options: Array<{ id: string; name: string }>;
  placeholder: string;
  disabled: boolean;
  onPick: (id: string) => void;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      disabled={disabled || options.length === 0}
      onChange={(e) => onPick(e.target.value)}
      className="min-h-11 w-full rounded-xl border border-divider bg-surface-primary px-3 py-2.5 text-base text-content-primary disabled:opacity-50"
    >
      {value === '' && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );
}

function HeaderButton({
  label,
  title,
  disabled,
  onClick,
  children,
}: {
  label: string;
  title?: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-2xl leading-none text-content-secondary hover:bg-interactive-hover hover:text-content-primary disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
    >
      <span aria-hidden="true">{children}</span>
    </button>
  );
}
